package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.LocaleReadinessDto
import dev.lingvo.shared.ReadinessDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.Async
import dev.lingvo.site.components.automation.PretranslateConfirm
import dev.lingvo.site.components.automation.PretranslateScope
import dev.lingvo.site.components.automation.openFirstQaError
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.ColorTokens
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCommentIcon
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LEllipsisIcon
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LEyeIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LMenu
import dev.lingvo.site.ds.LMenuEntry
import dev.lingvo.site.ds.LMenuItem
import dev.lingvo.site.ds.LProgressBar
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectAuthority
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.MouseEvent

const val READINESS_ROW_CAP = 5

/**
 * The label on the disclosure below a capped readiness list, or null when nothing is hidden.
 *
 * The rows arrive sorted worst-first (`Readiness.kt:76`), so the languages this hides are the ones
 * nearest shippable — the cap costs the reader nothing they were looking for. The server already
 * caps its own sentence at two named locales; this applies the same judgement to the rows.
 *
 * **`tPlural`, not `if (hidden == 1)`.** That ternary is English's rule wearing a general name, on
 * the surface a translator reads, in the product that sells getting plurals right — Polish needs a
 * third form at five and Japanese needs one. English renders identically; every other language now
 * has somewhere to put its own forms.
 */
fun readinessDisclosure(total: Int, shown: Int): String? {
    val hidden = total - shown
    return if (hidden <= 0) {
        null
    } else {
        tPlural(
            "readiness_band.show_more_languages",
            hidden,
            "Show {count} more language",
            "Show {count} more languages",
        )
    }
}

@Composable
internal fun ReadinessContent(
    slug: String,
    state: Async<ReadinessDto>,
    authority: ProjectAuthority,
    framed: Boolean,
    expanded: Boolean,
    onExpandedChange: (Boolean) -> Unit,
    onRetry: () -> Unit,
    onNavigate: (String) -> Unit,
    /**
     * A refresh that failed over readiness still on screen.
     *
     * Never the initial load's failure — that one has nothing to stand beside and stays in [state].
     * This is the reader pressing Refresh and the request not arriving, which must say so rather
     * than leave the previous counts on screen pretending to be current.
     */
    refreshError: String? = null,
    /**
     * Still the exact verified project and role that opened the menu.
     *
     * The QA lookup behind "Fix the first QA error" suspends and then navigates, and a navigation
     * published after a route or role change would land somebody on another project's editor.
     */
    remainsCurrent: () -> Boolean = { true },
    /**
     * The runs this band started, shared by every row in it.
     *
     * Null lets each row own its own store, which is what the page wants: a run belongs to the
     * language it was started for. A caller that hands one in — a test, or a surface that wants to
     * watch what the band started — sees every row's run in one place.
     */
    runs: ReadinessRunStore? = null,
) {
    var instantDisclosure by remember { mutableStateOf(false) }
    LAsyncContent(
        state,
        // The band's parent is a `flex-start` column, so an unsized wrapper would size to its
        // widest row and pull the progress bars in with it.
        modifier = Modifier.fillMaxWidth(),
        rows = 2,
        loadingLabel = t("readiness_band.checking_release_readiness", "Checking release readiness"),
        // The band collapses to a retry and the rest of the page renders. A slow readiness
        // query must not cost somebody the file list and the activity feed.
        //
        // Written out rather than left to `subject`/`onRetry` so the width above stops at the
        // success wrapper that needs it. A failure card is sized by its own content on every
        // other surface in the product, and a refactor is the wrong place for this band to
        // become the one that differs.
        failure = { fault ->
            AsyncFailureState(
                fault,
                subject = t("readiness_band.release_readiness", "release readiness"),
                onRetry = onRetry,
            )
        },
    ) { readiness ->
        // The summary and the rows were spaced by the parent column, and they are one element to it
        // now, so the spacing has to be here or they close up against each other.
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            if (refreshError != null) {
                LAlert(
                    title = t(
                        "readiness_band.showing_the_last_successful_readiness",
                        "Showing the last successful readiness check",
                    ),
                    description = refreshError,
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.WARNING,
                    // Only ever set by an explicit refresh over a band already on screen, so it is
                    // an answer to something the reader just pressed rather than an interruption.
                    live = true,
                    action = {
                        LButton(
                            label = t("readiness_band.retry", "Retry"),
                            onClick = onRetry,
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                        )
                    },
                )
            }
            P(
                Modifier
                    // A demotion, and deliberately so: a developer scanning for "which language is
                    // behind" reads the numbers in the rows below, not the sentence above them. The
                    // sentence used to be the loudest thing on the page at heading weight and size;
                    // now it states the headline once, quietly, and the rows carry every count.
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .fontWeight(400)
                    .margin(0.px)
                    // A two-line clamp, not `white-space: nowrap`. Nowrap let the sentence run past
                    // the section's own right edge into whatever sat beside it — "2 more languages
                    // a" with nothing after it, rather than a visible ellipsis, because there was no
                    // container narrower than the sentence to clip against. The clamp bounds it to
                    // the column it actually renders in and shows its own ellipsis on the second
                    // line; the full sentence still reaches anyone who needs it, in `title`.
                    .styleModifier {
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                    }
                    .toAttrs { attr("title", readiness.summary) },
            ) {
                Text(readiness.summary)
            }
            if (readiness.locales.isEmpty()) {
                val canManageLanguages = authority.canEditStructure
                LEmptyState(
                    title = if (canManageLanguages) {
                        t("readiness_band.add_a_target_language", "Add a target language")
                    } else {
                        t("readiness_band.no_target_languages_yet", "No target languages yet")
                    },
                    // Every branch wrapped: a `when` is an argument position no extractor run can
                    // reach, so the shell's best refusal — the one that tells a translator whose
                    // job it is to add a language — was English in every locale, on the surface a
                    // translator reads more than any other.
                    description = when (authority.role) {
                        Role.TRANSLATOR -> t(
                            "readiness_band.an_owner_or_admin_needs_to",
                            "An owner or admin needs to add a target language before you can translate.",
                        )
                        Role.VIEWER -> t(
                            "readiness_band.an_owner_or_admin_manages_target",
                            "An owner or admin manages target languages. You can review this project " +
                                "without changing its setup.",
                        )
                        else -> t(
                            "readiness_band.choose_the_first_language_this_project",
                            "Choose the first language this project needs to translate.",
                        )
                    },
                    modifier = Modifier.fillMaxWidth(),
                    icon = LEmptyStateIcon.LANGUAGE,
                ) {
                    if (canManageLanguages) {
                        LButton(
                            label = t("readiness_band.manage_languages", "Manage languages"),
                            onClick = { onNavigate("/app/projects/$slug/settings?tab=languages") },
                            variant = LButtonVariant.SECONDARY,
                        )
                    }
                }
            } else {
                val locales = readiness.locales
                val shown = if (expanded) locales.size else READINESS_ROW_CAP
                locales.take(shown).forEachIndexed { index, row ->
                    ReadinessRow(
                        slug,
                        row,
                        // The spend, named as a spend. `canEditStructure` applies the same
                        // owner-or-admin bar and reads at this call site as a claim about files.
                        canSpendCredits = authority.canSpendCredits,
                        canWriteTranslations = authority.canWriteTranslations,
                        onNavigate = onNavigate,
                        remainsCurrent = remainsCurrent,
                        runs = runs,
                        // Only what the disclosure just revealed animates. The first rows arrived
                        // with the page and are already on screen; running an entrance over them
                        // again on expand would animate content that did not move, and the reader
                        // would be looking for a change among rows that had none.
                        modifier = if (index >= READINESS_ROW_CAP && !instantDisclosure) {
                            Modifier.enterStaggered(index - READINESS_ROW_CAP)
                        } else {
                            Modifier
                        },
                    )
                }
                // **One button that toggles, not two that replace each other.** Expanding is
                // reversible — a one-way disclosure on a thirty-language project leaves the reader
                // scrolling past twenty-five rows they opened once and cannot put back — but the
                // reversal used to be a *second* button, so pressing either destroyed the element
                // that had focus and dropped a keyboard reader onto the document. One element that
                // survives the state change keeps the focus, and can carry `aria-expanded`, which
                // neither of the two ever did: the control said what it would do and never what it
                // had done.
                if (locales.size > READINESS_ROW_CAP) {
                    Div(attrs = {
                        // Click detail also covers assistive activation and programmatic clicks.
                        // This bubbles after the button toggles, before Compose draws the rows.
                        onClick { event ->
                            instantDisclosure = (event.nativeEvent as MouseEvent).detail == 0
                        }
                    }) {
                        LButton(
                            label = if (expanded) {
                                t("readiness_band.show_fewer_languages", "Show fewer languages")
                            } else {
                                readinessDisclosure(total = locales.size, shown = shown)
                                    ?: t("readiness_band.show_fewer_languages", "Show fewer languages")
                            },
                            onClick = { onExpandedChange(!expanded) },
                            modifier = Modifier.attr("aria-expanded", expanded.toString()),
                            variant = LButtonVariant.GHOST,
                        )
                    }
                }
            }
        }
    }
}

/**
 * The trailing action on a readiness row: what it says, what it announces, where it goes.
 *
 * The accessible label always names the language; the visible verb names the next useful step.
 */
enum class ReadinessPrimaryActionKind { TRANSLATE, QA, REVIEW, EXPORT }

data class ReadinessRowAction(
    val label: String,
    val accessibleLabel: String,
    val path: String,
    val kind: ReadinessPrimaryActionKind = ReadinessPrimaryActionKind.TRANSLATE,
)

fun readinessRowAction(slug: String, locale: String): ReadinessRowAction {
    val name = localeInfo(locale).englishName.takeIf { it != locale } ?: locale
    return ReadinessRowAction(
        label = tr("readiness_band.translate", "Translate"),
        accessibleLabel = trf(
            "readiness_band.translate_language",
            "Translate {language}",
            "language" to name,
        ),
        path = "/app/projects/$slug/translate/$locale",
    )
}

fun readinessRowAction(slug: String, row: LocaleReadinessDto, canWriteTranslations: Boolean = true): ReadinessRowAction {
    val language = localeInfo(row.locale).englishName.takeIf { it != row.locale } ?: row.locale
    val viewTranslations = ReadinessRowAction(
        tr("readiness_band.primary_view_translations", "View translations"),
        trf("readiness_band.primary_view_translations_language", "View translations in {language}", "language" to language),
        "/app/projects/$slug/translate/${row.locale}",
    )
    val editOrView = if (canWriteTranslations) readinessRowAction(slug, row.locale) else viewTranslations
    if (row.untranslated > 0) return editOrView
    return when {
        row.qaErrors > 0 -> ReadinessRowAction(
            if (canWriteTranslations) tr("readiness_band.primary_check_qa", "Check QA")
            else tr("readiness_band.primary_view_qa", "View QA"),
            if (canWriteTranslations) trf("readiness_band.primary_check_qa_language", "Check QA for {language}", "language" to language)
            else trf("readiness_band.primary_view_qa_language", "View QA for {language}", "language" to language),
            "/app/projects/$slug/qa?locale=${row.locale}",
            ReadinessPrimaryActionKind.QA,
        )
        row.awaitingReview > 0 -> ReadinessRowAction(
            if (canWriteTranslations) tr("readiness_band.primary_review", "Review") else viewTranslations.label,
            if (canWriteTranslations) trf("readiness_band.primary_review_language", "Review {language}", "language" to language)
            else viewTranslations.accessibleLabel,
            // Readiness counts every TRANSLATED string, including unsubmitted drafts. The
            // dedicated review lane only contains PENDING submissions, so use the matching set.
            "/app/projects/$slug/translate/${row.locale}?state=translated",
            ReadinessPrimaryActionKind.REVIEW,
        )
        row.shippable -> ReadinessRowAction(
            tr("readiness_band.primary_export", "Export"),
            trf("readiness_band.primary_export_language", "Export {language}", "language" to language),
            "/app/projects/$slug/data?locale=${row.locale}",
            ReadinessPrimaryActionKind.EXPORT,
        )
        else -> editOrView
    }
}

@Composable
private fun ReadinessPrimaryActionIcon(kind: ReadinessPrimaryActionKind) {
    when (kind) {
        ReadinessPrimaryActionKind.TRANSLATE -> LKeyIcon()
        ReadinessPrimaryActionKind.QA -> LWarningIcon()
        ReadinessPrimaryActionKind.REVIEW -> LEyeIcon()
        ReadinessPrimaryActionKind.EXPORT -> LDownloadIcon()
    }
}

/**
 * What the readiness row's overflow menu can offer, as a closed set.
 *
 * A `when` over this is what makes a new entry impossible to add without deciding what pressing it
 * does — the earlier version of this menu was a list of strings and a list of paths zipped by index.
 */
enum class ReadinessRowActionKind {
    PRETRANSLATE,
    REVIEW,
    FIX_FIRST_QA_ERROR,
    EXPORT,
}

/**
 * One row of the readiness menu: what it says, what it announces, and where it goes.
 *
 * [path] is null for the two entries that are not destinations:
 * [ReadinessRowActionKind.FIX_FIRST_QA_ERROR], whose target is a string the server has to name
 * before anybody can be sent to it, and [ReadinessRowActionKind.PRETRANSLATE], which opens the
 * estimate where it stands rather than navigating to a form that asks for the scope again.
 */
data class ReadinessRowMenuAction(
    val kind: ReadinessRowActionKind,
    val label: String,
    val accessibleLabel: String,
    val path: String?,
)

/**
 * The readiness row's menu, as data.
 *
 * **Every entry carries the count that motivates it and appears only when that count is above
 * zero.** "Review" over an empty queue is a control that opens a page saying there is nothing to do
 * — the reader presses it once per language before learning to distrust the menu. "Export" is the
 * exception and always present: exporting a finished language is exactly what somebody does when
 * every other counter is zero.
 *
 * **Pre-translate is absent, not disabled, below ADMIN.** `MtRoutes.requireSpendAllowed` refuses a
 * translator's run, and offering a control the server will refuse is the failure principle 6 names.
 * The privilege is the same one `ProjectAuthority.canEditStructure` reports, which is what the
 * caller passes in as [canSpendCredits].
 */
fun readinessRowMenu(
    slug: String,
    row: LocaleReadinessDto,
    canSpendCredits: Boolean,
    canWriteTranslations: Boolean = true,
): List<ReadinessRowMenuAction> {
    val language = localeInfo(row.locale).englishName.takeIf { it != row.locale } ?: row.locale
    fun named(kind: ReadinessRowActionKind, label: String, path: String?) = ReadinessRowMenuAction(
        kind = kind,
        label = label,
        accessibleLabel = trf(
            "readiness_band.menu_action_in_language",
            "{action}, {language}",
            "action" to label,
            "language" to language,
        ),
        path = path,
    )

    val actions = mutableListOf<ReadinessRowMenuAction>()
    if (canSpendCredits && row.untranslated > 0) {
        actions += named(
            ReadinessRowActionKind.PRETRANSLATE,
            tPlural(
                "readiness_band.menu.count.pretranslate",
                row.untranslated,
                "Pre-translate {count} untranslated string",
                "Pre-translate {count} untranslated strings",
            ),
            // No path: pressing this prices the run where it was asked for. Spec §2.5 — estimate
            // before, receipt after — and a deep link into the scope form asks the reader to state
            // a language and a filter the row already knows.
            null,
        )
    }
    if (row.awaitingReview > 0) {
        actions += named(
            ReadinessRowActionKind.REVIEW,
            if (canWriteTranslations) tPlural(
                "readiness_band.menu.count.review",
                row.awaitingReview,
                "Review {count} string",
                "Review {count} strings",
            ) else tPlural(
                "readiness_band.menu.count.view_unapproved",
                row.awaitingReview,
                "View {count} unapproved string",
                "View {count} unapproved strings",
            ),
            "/app/projects/$slug/translate/${row.locale}?state=translated",
        )
    }
    if (row.qaErrors > 0) {
        actions += named(
            ReadinessRowActionKind.FIX_FIRST_QA_ERROR,
            if (canWriteTranslations) tr("readiness_band.fix_the_first_qa_error", "Fix the first QA error")
            else tr("readiness_band.inspect_the_first_qa_error", "Inspect the first QA error"),
            null,
        )
    }
    actions += named(
        ReadinessRowActionKind.EXPORT,
        trf("readiness_band.menu.export_language", "Export {language}", "language" to language),
        "/app/projects/$slug/data?locale=${row.locale}",
    )
    return actions
}

/**
 * The glyph for one readiness menu entry, chosen by what the entry does.
 *
 * A `when` over the closed [ReadinessRowActionKind] rather than an icon passed in beside each
 * label, so an entry cannot be added without deciding what it looks like — the same reason the
 * kind exists at all.
 */
@Composable
private fun ReadinessRowActionIcon(kind: ReadinessRowActionKind) {
    when (kind) {
        ReadinessRowActionKind.PRETRANSLATE -> LWandSparklesIcon()
        ReadinessRowActionKind.REVIEW -> LEyeIcon()
        ReadinessRowActionKind.FIX_FIRST_QA_ERROR -> LWarningIcon()
        ReadinessRowActionKind.EXPORT -> LDownloadIcon()
    }
}

/**
 * One language, and five numbers that each go somewhere.
 *
 * **The counters are the follow-up questions, and every one of them already has a page that answers
 * it.** They used to be plain text inside a click handler on the whole row, so "12 QA errors" and
 * "40 awaiting review" both opened the unfiltered editor over every key in the project — four or
 * five steps to reach a list the system had computed before the click. That is what turns the
 * one-minute readiness check into a ten-minute one, which is the entire premise of this surface.
 *
 * Links rather than a clickable row: a `Column` with `onClick` is invisible to the keyboard and
 * announces nothing, and nesting the counters inside it would have nested interactive elements.
 *
 * **`row`'s counts are never a fabricated zero.** `Index.kt` used to build a `LocaleProgressDto`
 * with `total = project.keyCount` for a locale the project's own progress list omitted, so
 * "computed as zero" and "never computed" rendered identically. That cannot happen here:
 * `ReadinessRepo.counts` drives its query from `project_locales` via a `LEFT JOIN` against the
 * project's keys, so every target locale produces exactly one row — with real zeros when the
 * project has no keys yet, not an absence disguised as one. Asserted by
 * `ReadinessTest` on the server — "an untouched locale is counted, not omitted" and "every target
 * locale gets a row, even with no keys imported at all".
 *
 * **The row also starts work, so the row also shows it.** "Pre-translate 40 untranslated strings"
 * opens the estimate here rather than linking to a form that asks for the language again, and the
 * run it starts reports its progress on this row and its receipt in a toast — spec §6, and the
 * estimate-before/receipt-after rule in §2.5. Nothing about the row is replaced while a run goes:
 * the counts stay as they were last read, with the progress readout added beside them.
 */
@Composable
private fun ReadinessRow(
    slug: String,
    row: LocaleReadinessDto,
    canSpendCredits: Boolean,
    canWriteTranslations: Boolean,
    onNavigate: (String) -> Unit,
    remainsCurrent: () -> Boolean,
    runs: ReadinessRunStore?,
    modifier: Modifier = Modifier,
) {
    val language = localeInfo(row.locale).englishName
    val scope = rememberCoroutineScope()
    // A row only ever exists inside a routed page — its counters are Kobweb `Link`s, which read the
    // page context themselves — so the router the shared receipt toast needs is already here. The
    // toast channel comes from the shell rather than `LocalToasts`, which the band cannot guarantee
    // is provided: absent, the run still starts and still draws its progress here.
    val router = rememberPageContext().router
    val toasts = ShellToastHost.state
    val store = remember(runs, slug, router, toasts) {
        runs ?: ReadinessRunStore(slug, scope, toasts, router)
    }
    // Only a store this row made is a store this row may stop. Polling also ends on its own — the
    // flow completes on the first non-RUNNING answer, and `scope` is cancelled with the composition
    // — so this is the explicit half of "stop on terminal or leaving composition".
    val ownedStore = if (runs == null) store else null
    DisposableEffect(ownedStore) { onDispose { ownedStore?.dispose() } }
    var menuOpen by remember(row.locale) { mutableStateOf(false) }
    var resolvingQa by remember(row.locale) { mutableStateOf(false) }
    var qaLookup by remember(row.locale) { mutableStateOf<Job?>(null) }
    var confirmingRun by remember(row.locale) { mutableStateOf(false) }
    val menu = readinessRowMenu(slug, row, canSpendCredits, canWriteTranslations)
    // **The captured claim, kept.** `openFirstQaError` navigates the moment the server answers, so
    // the guard cannot be a check after the suspension — it has to retire the request itself.
    // Recomposition is the only moment the route, the session or the role can have changed, so a
    // lookup whose claim no longer holds is cancelled here rather than allowed to resume into a
    // project the reader is no longer looking at.
    SideEffect {
        if (qaLookup?.isActive == true && !remainsCurrent()) {
            qaLookup?.cancel()
            qaLookup = null
            resolvingQa = false
        }
    }

    Column(Modifier.fillMaxWidth().gap(Space.XS).then(modifier).attr("data-readiness-locale", row.locale)) {
        Row(
            Modifier.fillMaxWidth().gap(Space.SM),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // Decorative: the language is named in the link immediately beside it, so announcing
            // the flag too would say the language twice per row.
            LocaleFlag(row.locale, decorative = true)
            // The language is the row's subject. Navigation belongs to the counted work and the
            // explicit next action, which can be review, QA or export rather than translation.
            Span(Modifier.fontWeight(600).toAttrs()) {
                Text(language)
            }
            // Only when the flag slot actually drew a flag. `LocaleFlag` falls back to printing
            // the bare code whenever the locale names no country (Common.kt:94) — which is every
            // locale in a language-only project — and the mono code four words later then repeated
            // it: "ru Russian ru", on every row. The code is shown exactly once either way.
            if (localeFlagPrefix(row.locale).isNotBlank()) {
                Span(
                    Modifier
                        .monoFont()
                        .color(ColorMode.current.toSitePalette().textMuted)
                        .fontSize(TypeScale.Small)
                        .toAttrs(),
                ) {
                    Text(row.locale)
                }
            }
            if (row.shippable) {
                LBadge(t("readiness_band.release_ready", "Release-ready"), variant = LBadgeVariant.SUCCESS)
            }
            Spacer()
            val action = readinessRowAction(slug, row, canWriteTranslations)
            LButton(
                label = action.label,
                accessibleLabel = action.accessibleLabel,
                // `onNavigate`, not `window.location.href`. This is an internal route, and
                // assigning to `location` reloads the whole document: the reader waits for the app
                // to boot again, and every other control on this band already routes in place.
                onClick = { onNavigate(action.path) },
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                leadingIcon = { ReadinessPrimaryActionIcon(action.kind) },
            )
            // Everything else this language can be taken to, behind one glyph rather than five
            // buttons per row: five languages already put fifteen counters on screen, and a row of
            // named actions beside each of them is the point at which the band stops being
            // scannable at all.
            LMenu(
                expanded = menuOpen,
                // The glyph belongs to the kind, not to the call site: `ReadinessRowActionKind`
                // already names which of the four things an entry is, so the drawing is a `when`
                // over it rather than a list zipped by index. Without it this was the one menu in
                // the pass that was four lines of unadorned text beside three that were not.
                entries = menu.map { entry ->
                    LMenuItem(entry.label, icon = { ReadinessRowActionIcon(entry.kind) }) as LMenuEntry
                },
                onExpandedChange = { menuOpen = it },
                onSelect = { index ->
                    val entry = menu.getOrNull(index) ?: return@LMenu
                    when (entry.kind) {
                        // The price, then the run, then the receipt — here, on the row that states
                        // the count. This used to be a link into the scope form, which asked the
                        // reader to choose the language they had just pressed a button about.
                        ReadinessRowActionKind.PRETRANSLATE -> confirmingRun = true

                        ReadinessRowActionKind.FIX_FIRST_QA_ERROR -> {
                            if (remainsCurrent()) {
                                resolvingQa = true
                                // One implementation of "fix the first QA error" for the readiness
                                // row, the QA header, the dashboard tile and the editor: the same
                                // request, the same destination, and the same honest answer — a
                                // toast — when the server reports none left.
                                qaLookup = scope.launch {
                                    try {
                                        openFirstQaError(
                                            router = router,
                                            projectSlug = slug,
                                            locale = row.locale,
                                            toasts = toasts,
                                        )
                                    } finally {
                                        resolvingQa = false
                                    }
                                }
                            }
                        }

                        else -> entry.path?.let(onNavigate)
                    }
                },
            ) { onToggle, triggerModifier ->
                LIconAction(
                    label = trf(
                        "readiness_band.more_actions_for_language",
                        "More actions for {language}",
                        "language" to language,
                    ),
                    onClick = onToggle,
                    modifier = triggerModifier,
                    size = LButtonSize.SM,
                    loading = resolvingQa,
                ) { LEllipsisIcon() }
            }
        }
        // A continuous track rather than the twenty-cell bar the dashboard used to share with the
        // project list: five languages of cells and five columns of counters was two dense
        // instruments stacked on the same row, and the cells said less than the percentage now
        // printed beside them. The colours are unchanged — `LProgressBar(compact = true)` fills
        // from the same `ChartPalette.forState` approved/translated hues the twenty-cell bar drew
        // its own cells in, so "green" still means the same thing everywhere this page draws it.
        ReadinessRowBar(row)
        // **The run stays on the row that started it.** Nothing here blanks or replaces the row —
        // the counters keep their last true values until the reload after the run — so this is a
        // readout added beside them rather than a page that changes underneath somebody. It names
        // its language, because five rows can each have one.
        val run = store.runFor(row.locale)
        if (store.isRunning(row.locale)) {
            LTaskProgress(
                done = run?.done ?: 0,
                total = run?.total ?: 0,
                // A string the provider refused is not a string still to do, and a progress bar
                // that counts it as pending never reaches its own total.
                failed = (run?.failed ?: 0) + (run?.rejected ?: 0),
                label = tf(
                    "readiness_band.pre_translate_run_in_language",
                    "Pre-translate run in {language}",
                    "language" to language,
                ),
                // Between the id coming back and the first answer there is a run with no shape:
                // `aria-busy` and a hairline say so, rather than a bar drawing 0 of 0.
                indeterminate = run == null,
            )
        }
        // Every linked counter names its language in its accessible name. Five languages produce
        // up to fifteen links, and without this a screen-reader link list reads "40 Untranslated"
        // five times over with nothing saying which language each belongs to — the same WCAG 2.4.4
        // shape the row's Translate button solves two lines above.
        //
        // `Counter` is not an argument position any extractor run can reach, so these five words —
        // the whole vocabulary of the readiness check — were English in every locale.
        Row(Modifier.fillMaxWidth().gap(Space.LG).flexWrap(FlexWrap.Wrap)) {
            Counter(
                t("readiness_band.untranslated", "Untranslated"),
                row.untranslated,
                language,
                "/app/projects/$slug/translate/${row.locale}",
            ) { LLanguagesIcon() }
            Counter(
                t("readiness_band.awaiting_review", "Awaiting review"),
                row.awaitingReview,
                language,
                "/app/projects/$slug/translate/${row.locale}?state=translated",
            ) { LEyeIcon() }
            Counter(
                t("readiness_band.qa_errors", "QA errors"),
                row.qaErrors,
                language,
                "/app/projects/$slug/qa?locale=${row.locale}",
            ) { LWarningIcon() }
            // No surface exists that lists only these two, so they stay plain rather than promise
            // a filtered page and deliver an unfiltered one.
            Counter(t("readiness_band.blocked", "Unanswered"), row.blocked, language) { LCommentIcon() }
            Counter(
                t("readiness_band.machine_unreviewed", "Machine, unreviewed"),
                row.machineUnreviewed,
                language,
            ) { LBotIcon() }
        }
        // Mounted by the row, outside the menu: selecting an entry closes the menu, and a dialog
        // living inside it would go down with it. Nothing is requested until `confirmingRun`, so
        // opening the menu prices nothing.
        //
        // `onStarted` cannot tell a fresh run from the one a `409 job_already_running` named, and
        // must not: either way the work is happening and the honest answer is its progress.
        PretranslateConfirm(
            open = confirmingRun,
            slug = slug,
            locales = listOf(row.locale),
            scope = PretranslateScope(onlyEmpty = true),
            onStarted = { jobId -> store.attach(row.locale, jobId) },
            onClose = { confirmingRun = false },
        )
    }
}

/**
 * One language's approved share, as a continuous 8px track rather than twenty discrete cells.
 *
 * The percentage printed beside it is *approved*, not "translated or approved": a machine string
 * nobody has read is not release-ready, and a bar that filled on it would draw a language closer to
 * shippable than [LocaleReadinessDto.shippable] itself says it is.
 */
@Composable
private fun ReadinessRowBar(row: LocaleReadinessDto) {
    val palette = ColorMode.current.toSitePalette()
    val total = row.total.coerceAtLeast(0)
    val approved = row.approved.coerceIn(0, total)
    val awaitingReview = row.awaitingReview.coerceIn(0, total - approved)
    val approvedPercent = if (total == 0) 0 else (approved * 100 / total)
    // A grid, not a flex row with a `minWidth` on the trailing span: `minWidth` is a floor, not a
    // fixed column, so the percentage's box grew or shrank with its own digit count and the row's
    // right edge wandered relative to the title row's buttons above it — "4%" ended well short of
    // "100%". `3.5em` is a fixed column instead, sized in the number's own unit so it survives a
    // font change, and it is the same width on every row regardless of what the row prints in it.
    Div(
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr) 3.5em")
                property("column-gap", Space.SM.toString())
                property("align-items", "center")
            }
            .toAttrs(),
    ) {
        LProgressBar(
            progress = LProgressCounts(total = total, translated = awaitingReview, approved = approved),
            modifier = Modifier.minWidth(0.px),
            label = trf(
                "readiness_band.language_translation_progress",
                "{language} translation progress",
                "language" to localeInfo(row.locale).englishName,
            ),
            showCounts = false,
            compact = true,
        )
        Span(
            Modifier
                .tabularNumbers()
                .fontSize(TypeScale.Small)
                .fontWeight(600)
                .color(palette.textSecondary)
                .textAlign(TextAlign.End)
                .toAttrs(),
        ) { Text("$approvedPercent%") }
    }
}

/**
 * One icon, one number, one short noun — and nothing at all when the number is zero.
 *
 * Used to be an `LStatTile(compact)` per counter: a figure over a caption, five to a row, so a
 * settled language and a broken one took the identical five columns and the difference was only
 * which figures read zero. A row now states its overall share with [ReadinessRowBar] and prints
 * only the counters that are still real work, each as a single inline icon-and-number pair in the
 * fixed order the call site already keeps: untranslated, awaiting review, QA errors, unanswered,
 * machine.
 */
@Composable
private fun Counter(
    label: String,
    value: Int,
    language: String,
    href: String? = null,
    icon: @Composable () -> Unit,
) {
    // Zero is the good outcome, and the bar and the "Release-ready" badge already say so. Five
    // languages used to print all five counters regardless — a finished German took the same five
    // columns as a broken French, with zero repeated across every settled column. Now a settled
    // counter draws nothing at all, and what remains on the row is only real work.
    if (value <= 0) return
    val palette = ColorMode.current.toSitePalette()
    val text = "${formatCount(value)} $label"
    val body: @Composable () -> Unit = {
        Row(Modifier.gap(Space.XXS), verticalAlignment = Alignment.CenterVertically) {
            Span(
                Modifier
                    .iconSize(IconSize.XS)
                    .color(palette.textSecondary)
                    .styleModifier { property("display", "inline-flex") }
                    .toAttrs { attr("aria-hidden", "true") },
            ) { icon() }
            Span(
                Modifier.tabularNumbers().fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs(),
            ) { Text(text) }
        }
    }
    // Names the language in its accessible name whether or not it links anywhere: two languages
    // with equal counts must not offer two identically-announced elements.
    val accessibleLabel = trf(
        "readiness_band.counter_label",
        "{value} {label}, {language}",
        "value" to value,
        "label" to label,
        "language" to language,
    )
    if (href != null) {
        Link(
            path = href,
            modifier = PlainLinkStyle.toModifier().attr("aria-label", accessibleLabel),
        ) { body() }
    } else {
        Row(Modifier.attr("aria-label", accessibleLabel), verticalAlignment = Alignment.CenterVertically) {
            body()
        }
    }
}

/**
 * A link that does not look like one.
 *
 * The band is read as a wall of numbers; five rows of blue underlined text would drown the one
 * signal it exists to carry. Colour is inherited so the muted-zero rule above still shows through,
 * and focus stays visible because the browser's outline is untouched.
 *
 * Used by the keys table's name cell, the dashboard's project rows, and this band's own linked
 * counters.
 */
val PlainLinkStyle = CssStyle {
    base { Modifier
            .styleModifier { property("color", "inherit") }
            .textDecorationLine(TextDecorationLine.None) }
    hover { Modifier.textDecorationLine(TextDecorationLine.Underline) }
}

/**
 * A counter that is a link, and looks like one without a pointer over it.
 *
 * `ColorTokens.TextMuted` is the muted underline colour, already verified at 4.5:1 on every ground
 * in `PaletteContrastTest` — well past the 3:1 floor a decorative underline needs.
 */
val LinkedCounterStyle = CssStyle {
    base {
        Modifier
            .styleModifier { property("color", "inherit") }
            .textDecorationLine(TextDecorationLine.Underline)
            .styleModifier {
                property("text-decoration-color", ColorTokens.TextMuted.value())
                property("text-underline-offset", "3px")
            }
    }
    hover {
        Modifier.styleModifier { property("text-decoration-color", "currentColor") }
    }
}
